import { useTextfield } from "@albo-ui/core";
import { useEffect, useMemo, useRef, useState } from "react";
import { LeadService } from "../../../../services/LeadService";
import { useCatalog } from "../../../../util/hooks/useCatalog";
import { LeadDataType } from "../../../customwidgets/leads-tracking-report/types";
import { CrmService } from "../../../../services/CrmService";
import { isContractSentStatus } from "../../../customwidgets/leads-onboarding-sections/pldStatus";

export const useChangeStatusLead = ( onConfirm: () => void, onClose: () => void, open: boolean, changeAlert, lead: LeadDataType, config: any ) => {
    const leadService = useRef<LeadService>();
    const crmService = useRef<CrmService>();
    const [statusSelected, setStatusSelected] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const [statusCatalog] = useCatalog("abb_status_lead", changeAlert);
    const [selectOccupation, setSelectOccupation] = useState(null);
    const [editField, setEditField] = useState(null);
    const [payloadCreate, setPayloadCreate] = useState(null);
    const [fee, setFee] = useState(null);
    const [isForeigner, setIsForeigner] = useState(false);
    const customerUuidProps = useTextfield();

    const orderedStatus = useMemo(() => {
        const orderItems = [...statusCatalog]
            .filter((s) => s.metadata.is_search)
            .sort((a, b) => (a.metadata.order > b.metadata.order ? 1 : -1));
        return orderItems;
    }, [statusCatalog]);

    const dataCurrentStatus = useMemo(
        () => orderedStatus?.find((s) => s.name === lead.status),
        [lead, orderedStatus]
    );

    const hasAction = (action) => {
        return config?.actions?.indexOf(action) > -1;
    };

    const isDone = statusSelected === "done";
    const isInvalidStatus = isDone && ((hasAction("autoClabe") && lead.type === "PM") ? false : customerUuidProps.value === "");

    useEffect(() => {
        if(!open){
            customerUuidProps.onInput('')
            setPayloadCreate(null);
            setSelectOccupation(null);
            setEditField(null);
        }
        leadService.current = new LeadService(changeAlert);
        crmService.current = new CrmService(changeAlert);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        setStatusSelected(lead.status);
    }, [lead.status]);

    const onSelectStatus = (e) => {
        const nextStatus = e.target.value;
        if (canNextStatus(nextStatus)) {
            setStatusSelected(e.target.value);
            if (hasAction("sendFee") && e.target.value === "signing") {
                onFindComission();
            } else {
                setFee(JSON.parse(JSON.stringify(null)));
            }
        }
    };
    const canNextStatus = (status) => {
        const canChange = dataCurrentStatus?.metadata?.next_status?.includes(status) || dataCurrentStatus?.name === status;
        return canChange;
    };
    const onHandleChange = (key1, key2, e) => {
        let p = payloadCreate;
        let value = e.target.value;
        p[key1][key2] = value;

        setPayloadCreate(JSON.parse(JSON.stringify(p)));
    };

    const onFindComission = async () => {
        setLoading(true);

        let f = fee;
        let findComission = await onHandleFindCommission(lead.uuid);
        if (Object.keys(findComission).length !== 0) {
            f = {
                type: findComission["charge_fee"] ? "WITH_FEE" : "NO_FEE"
            };
            if (findComission["charge_fee"]) {
                f["metadata"] = {
                    percentage_fee: findComission["charge_fee"]
                }
            }
            if (!findComission['general']['segment'] || !findComission['general']['company_group']) {
                changeAlert(true, "error", "Error: El Lead no posee Segmento y/o Grupo Empresarial asignado. Ingrese al Panel CRM para realizar la asignación correspondiente.");
                onClose();
            }
        } else {
            changeAlert(true, "error", "Error: El Lead no se encuentra registrado en el CRM o el UUID es erróneo. Ingrese al Panel CRM para realizar la verificación.");
            onClose();
        }
        setFee(JSON.parse(JSON.stringify(f)));
        setLoading(false);
    };

    const onConfirmChangeStatus = async () => {
        setLoading(true);

        if (statusSelected === "done" && lead.type === "PM" && hasAction("afbCreate")) {
            return customerCreate();
        } else {
            const selectedStatus = orderedStatus?.find((s) => s.name === statusSelected);
            let payload = {
                lead_uuid: lead.uuid,
                new_status: statusSelected,
                customer_uuid: customerUuidProps.value,
                is_contract_sent: isContractSentStatus(selectedStatus)
            }

            if (fee) {
                payload["agreement"] = fee;
            }

            const resp = await leadService.current.updateStatus(payload);
            onConfirm()
            setLoading(false);
            if(resp?.data?.response==='OK'){
                changeAlert(true,"OK", "Estatus actualizado correctamente.");
            }
        }
    };

    const customerCreate = async () => {
        setLoading(true);
        
        if (!payloadCreate) {
            Promise.all([
                leadService.current.getOnboardingSectionLead({
                    lead_uuid: lead.uuid,
                    section_name: "COMPANY_INFO",
                }),
                leadService.current.getOnboardingSectionLead({
                    lead_uuid: lead.uuid,
                    section_name: "RISK_LEVEL",
                }),
                leadService.current.getOnboardingSectionLead({
                    lead_uuid: lead.uuid,
                    section_name: "LEAD_DETAILS",
                }),
                crmService.current.leadCrmGet({filters:
                    { reference_uuid: lead.uuid }
                })
            ])
                .then((results) => {
                    let general = results[0].data.content.details;
                    let risk = results[1].data.content.details["risk_level"];
                    let details = results[2].data.content.details;
                    let crm = results[3].data.content;


                    let payload = {
                        general: {
                            email: general["email"],
                            bcountry: general["constitution_country"],
                            bdate: general["constitution_date"],
                            bstate: isForeigner ? "33" : general["address"]["state"],
                            gname: general["comercial_name"],
                            lname: general["social_reason"],
                            occupation: general["economic_activity"] ? general["economic_activity"] : general["economic_activity_desc"],
                            phone: general["phone"]["phone_code"] + general["phone"]["phone"],
                            taxid: general["taxid"],
                            populationid: general["taxid"],
                            corporate_name: general["social_reason"],
                        },
                        address: {
                            country: general["address"]["country"],
                            extnum: general["address"]["extnum"],
                            intnum: general["address"]["intnum"] || "",
                            locality: general["address"]["locality_desc"],
                            state: isForeigner ? "33" : general["address"]["state"],
                            street: general["address"]["street"],
                            town: general["address"]["town"],
                            zip: isForeigner ? "06700" : general["address"]["zip"],
                        },
                        space: {
                            name: details["account_name"] || "Cuenta principal",
                            description: details["main_use"],
                            clabe: customerUuidProps.value,
                            cc: "INTELIGENCIA_FI",
                            account_number: general["account_number"],
                        },
                        identity: {
                            validation_result: {
                                risk_level: {
                                    score: 0,
                                    warning_results: false,
                                    score_level: risk,
                                },
                            },
                        },
                    };

                    if(general["geolocation"] && general["geolocation"]["latitude"] && general["geolocation"]["longitude"]){
                        payload['address']['geolocation'] = {
                            latitude: general["geolocation"]["latitude"] || "0.0",
                            longitude: general["geolocation"]["longitude"] || "0.0",
                        };
                    }
                    
                    if(crm && crm['general']){
                        payload['origin'] = {
                            segment: crm['general']['segment'],
                            company_group: crm['general']['company_group']
                        }
                    }

                    hasAction("autoClabe") && delete payload["space"]["clabe"]
                    setPayloadCreate(payload);

                    if(!risk){
                        return changeAlert(true, "error", "Error: El Nivel de Riesgo no ha sido calculado");
                    }

                    if(general["economic_activity"] && Number(general["economic_activity"])){
                        setSelectOccupation(null)
                        return onSendRequest(payload);
                    } else {
                        setSelectOccupation(general["economic_activity"] || "Sin resultados")
                    }
                })
                .catch((error) => {
                    changeAlert(true, "error", "Error: " + error);
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            return onSendRequest();
        }
    };

    const onSendRequest = async (payload = payloadCreate) => {
        setEditField(null);

        if(!hasAction("autoClabe") && customerUuidProps.value){
            payload["space"]["clabe"] = customerUuidProps.value;
        }
        
        const respCreate = await leadService.current.createAfbCustomer({
            data: payload,
        });
        if (respCreate?.data?.response === "OK") {
            changeAlert(true, "OK", "Cliente creado con exito.");

            const respUpdate = await leadService.current.updateStatus({
                lead_uuid: lead.uuid,
                new_status: statusSelected,
                customer_uuid: hasAction("autoClabe") ? respCreate.data.data["customer"]["uuid"] : respCreate.data.data.messageUUID,
            });

            onConfirm()
            setLoading(false);

            if (respUpdate?.data?.response === "OK") {
                changeAlert(true, "OK", "Estatus actualizado correctamente.");
            }
        } else {
            switch (respCreate.data?.code || respCreate.data?.exception?.code) {
                case "0003.003.025":
                case "0003.003.036":
                    setEditField({key:"phone", label: "Teléfono"});
                    break;
                case "0003.003.026":
                case "0003.003.037":
                    setEditField({key:"email", label: "Correo"});
                    break;
                default:
                    break;
            }
        }
        setLoading(false);
    };

    const onHandleFindCommission = async (key) => {
        const payload = { filters: { reference_uuid: key }}

        const response = await crmService.current.leadCrmGet(payload);
        if (response["data"]?.response === "OK") {
            return response.data["content"] ? response.data["content"] : false;
        } else {
            changeAlert(true, "error", response["data"].message || "Error al consultar servicio de comisiones.");
            return false;
        }
    };


    return {
        status: orderedStatus,
        statusSelected,
        customerUuidProps,
        isInvalidStatus,
        isDone,
        loading,
        selectOccupation,
        payloadCreate,
        editField,
        fee,
        isForeigner,
        setIsForeigner,
        onSelectStatus,
        canNextStatus,
        onConfirmChangeStatus,
        onHandleChange,
        hasAction
    };
};
